import {
  ArrowUpCircle,
  ChefHat,
  Clock,
  Shirt,
  X,
  BedDouble,
  Bed,
  Sofa,
  LampDesk,
  Utensils,
  TvMinimal,
  LampFloor,
  Warehouse,
  BrickWall,
  Package,
  Zap,
  CircleDot,
} from "@tamagui/lucide-icons";
import {
  Sensor,
  SensorAlert,
  SensorAlertSeverity,
  SensorAlertType,
  SensorStatus,
} from "common";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  YStack,
  Text,
  Heading,
  XStack,
  Separator,
  Circle,
  View,
  Button,
  Label,
} from "tamagui";
import { GradientButton } from "tamagui-config";

import { GradientCard } from "../components/GradientCard";
import { ElectricityHome, WaterLeak } from "../components/icons";
import { HVAC } from "../components/icons/HVAC";
import { KPICard } from "../components/KPICard";
import { Select } from "../components/Select";
import { SensorCard } from "../components/SensorCard";
import { useGetEnergySavingData } from "../hooks/electrical/useGetEnergySavingData";

const Dashboard = () => {
  const { t } = useTranslation();

  const locationFilterOptions = useMemo(
    () => [
      { label: t("common.all"), value: "" },
      ...mockedSensorLocations.map((location) => ({
        label: location.name,
        value: location.id,
      })),
    ],
    [mockedSensorLocations],
  );
  const typeFilterOptions = [{ label: t("common.all"), value: "" }];
  const statusFilterOptions = [
    { label: t("common.all"), value: "" },
    { label: t("sensors.statusValue.active"), value: SensorStatus.Active },
    { label: t("sensors.statusValue.inactive"), value: SensorStatus.Inactive },
    {
      label: t("sensors.statusValue.maintenance"),
      value: SensorStatus.Maintenance,
    },
    { label: t("sensors.statusValue.pending"), value: SensorStatus.Pending },
  ];

  const [selectedLocationFilter, setSelectedLocationFilter] = useState(
    locationFilterOptions[0].value,
  );
  const [selectedTypeFilter, setSelectedTypeFilter] = useState(
    typeFilterOptions[0].value,
  );
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(
    statusFilterOptions[0].value,
  );

  const [locationFilter, setLocationFilter] = useState(
    locationFilterOptions[0].value,
  );
  const [typeFilter, setTypeFilter] = useState(typeFilterOptions[0].value);
  const [statusFilter, setStatusFilter] = useState(
    statusFilterOptions[0].value,
  );
  const filterCount = useMemo(() => {
    let count = 0;
    if (locationFilter && locationFilter !== locationFilterOptions[0].value) {
      count++;
    }
    if (typeFilter && typeFilter !== typeFilterOptions[0].value) {
      count++;
    }
    if (statusFilter && statusFilter !== statusFilterOptions[0].value) {
      count++;
    }
    return count;
  }, [
    locationFilter,
    locationFilterOptions,
    typeFilter,
    typeFilterOptions,
    statusFilter,
    statusFilterOptions,
  ]);

  const { value: energySavingValue } = useGetEnergySavingData(14);

  const sensorLocationsWithData = mockedSensorLocations.map((location) => ({
    ...location,
    sensors: mockedSensors
      .filter((sensor) => sensor.location === location.id)
      .filter((sensor) => {
        const matchesLocation =
          !locationFilter || sensor.location === locationFilter;
        const matchesType = !typeFilter || sensor.type === typeFilter;
        const matchesStatus = !statusFilter || sensor.status === statusFilter;
        return matchesLocation && matchesType && matchesStatus;
      })
      .map((sensor) => ({
        ...sensor,
        alerts: mockedSensorAlerts.filter(
          (alert) => alert.sensorId === sensor.id,
        ),
      })),
  }));

  const applyFilters = () => {
    setLocationFilter(selectedLocationFilter);
    setTypeFilter(selectedTypeFilter);
    setStatusFilter(selectedStatusFilter);
  };

  const clearFilters = () => {
    setLocationFilter(locationFilterOptions[0].value);
    setTypeFilter(typeFilterOptions[0].value);
    setStatusFilter(statusFilterOptions[0].value);

    setSelectedLocationFilter(locationFilterOptions[0].value);
    setSelectedTypeFilter(typeFilterOptions[0].value);
    setSelectedStatusFilter(statusFilterOptions[0].value);
  };

  return (
    <YStack gap="$5">
      <Heading fontSize="$8" lineHeight="$7">
        {t("dashboard.title")}
      </Heading>

      <XStack gap="$5">
        <View flexGrow={1} flexBasis={0}>
          <KPICard
            error
            icon={WaterLeak}
            title={mockedKpis.leakDetected ? 1 : 0}
            description={t("dashboard.leakDetected")}
          />
        </View>
        <View flexGrow={1} flexBasis={0}>
          <KPICard
            icon={ElectricityHome}
            title={
              <XStack gap="$2" alignItems="center">
                <ArrowUpCircle color="$successGreen" />
                <Heading
                  fontSize="$4"
                  lineHeight="$3.5"
                  color="$white12"
                  alignItems="center"
                  overflow="hidden"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                >
                  {t("common.currency", {
                    value: energySavingValue || mockedKpis.energySaving,
                  })}
                </Heading>
              </XStack>
            }
            description={t("dashboard.energySaving")}
          />
        </View>
        <View flexGrow={1} flexBasis={0}>
          <KPICard
            icon={Clock}
            title={t("dashboard.lastUpdatedValue", {
              date: mockedKpis.lastUpdated,
            })}
            description={t("dashboard.lastUpdated")}
          />
        </View>
      </XStack>

      <GradientCard backgroundColor="$transparentBorder" backgroundImage="none">
        <XStack gap="$3" alignItems="center" justifyContent="space-between">
          <XStack gap="$2.5" alignItems="center">
            <Text fontWeight="bold" color="$white12">
              {t("dashboard.filters.heading")}
            </Text>
            <Circle size={24} backgroundColor="$transparentBorder">
              <Text fontSize="$1" fontWeight="bold">
                {filterCount}
              </Text>
            </Circle>
          </XStack>
          <Button
            variant="outlined"
            height={36}
            paddingVertical={0}
            paddingHorizontal="$3"
            onPress={clearFilters}
          >
            <X size={20} />
            {t("dashboard.filters.clear")}
          </Button>
        </XStack>

        <XStack gap="$3.5">
          <YStack gap="$2" flexGrow={1} flexBasis={0}>
            <Label
              htmlFor="location-filter"
              fontSize="$3"
              lineHeight="$2"
              color="$white10"
            >
              {t("dashboard.filters.location")}
            </Label>
            <Select
              id="location-filter"
              options={locationFilterOptions}
              value={selectedLocationFilter}
              onValueChange={setSelectedLocationFilter}
            />
          </YStack>
          <YStack gap="$2" flexGrow={1} flexBasis={0}>
            <Label
              htmlFor="type-filter"
              fontSize="$3"
              lineHeight="$2"
              color="$white10"
            >
              {t("dashboard.filters.type")}
            </Label>
            <Select
              id="type-filter"
              options={typeFilterOptions}
              value={selectedTypeFilter}
              onValueChange={setSelectedTypeFilter}
            />
          </YStack>
          <YStack gap="$2" flexGrow={1} flexBasis={0}>
            <Label
              htmlFor="status-filter"
              fontSize="$3"
              lineHeight="$2"
              color="$white10"
            >
              {t("dashboard.filters.status")}
            </Label>
            <Select
              id="status-filter"
              options={statusFilterOptions}
              value={selectedStatusFilter}
              onValueChange={setSelectedStatusFilter}
            />
          </YStack>
          <GradientButton
            elevated
            rounded
            alignSelf="flex-end"
            onPress={applyFilters}
          >
            {t("dashboard.filters.apply")}
          </GradientButton>
        </XStack>
      </GradientCard>

      <Separator />

      {sensorLocationsWithData.map(({ sensors, ...location }) => (
        <YStack key={location.id} gap="$4">
          <XStack gap="$3.5" alignItems="center">
            <Circle size={38} backgroundColor="$transparentBorder">
              {renderIcon(location.type)}
            </Circle>
            <Heading fontSize="$4" lineHeight="$2">
              {location.name}
            </Heading>
          </XStack>

          {Array.from({ length: Math.ceil(sensors.length / 3) }).map(
            (_, rowIndex) => (
              <XStack key={rowIndex} gap="$5">
                {sensors.slice(rowIndex * 3, rowIndex * 3 + 3).map((sensor) => (
                  <View key={sensor.id} flexGrow={1} flexBasis={0}>
                    <SensorCard sensor={sensor} />
                  </View>
                ))}
                {Array.from({
                  length:
                    3 - sensors.slice(rowIndex * 3, rowIndex * 3 + 3).length,
                }).map((_, i) => (
                  <View key={`spacer-${i}`} flexGrow={1} flexBasis={0} />
                ))}
              </XStack>
            ),
          )}
        </YStack>
      ))}
    </YStack>
  );
};

enum SensorLocationType {
  Kitchen = "kitchen",
  Laundry = "laundry",
  HVAC = "hvac",
  MasterBedroom = "master-bedroom",
  LivingRoom = "living-room",
  GuestBedroom = "guest-bedroom",
  Bedroom = "bedroom",
  DiningRoom = "dining-room",
  Office = "office",
  FamilyRoom = "family-room",
  MainFloor = "main-floor",
  Garage = "garage",
  Basement = "basement",
  Attic = "attic",
  ElectricalPanel = "electrical-panel",
  Custom = "custom",
}

const renderIcon = (locationType: SensorLocationType) => {
  switch (locationType) {
    case SensorLocationType.Kitchen:
      return <ChefHat />;
    case SensorLocationType.Laundry:
      return <Shirt />;
    case SensorLocationType.HVAC:
      return <HVAC />;
    case SensorLocationType.MasterBedroom:
      return <BedDouble />;
    case SensorLocationType.LivingRoom:
      return <Sofa />;
    case SensorLocationType.GuestBedroom:
      return <Bed />;
    case SensorLocationType.Bedroom:
      return <Bed />;
    case SensorLocationType.DiningRoom:
      return <Utensils />;
    case SensorLocationType.Office:
      return <LampDesk />;
    case SensorLocationType.FamilyRoom:
      return <TvMinimal />;
    case SensorLocationType.MainFloor:
      return <LampFloor />;
    case SensorLocationType.Garage:
      return <Warehouse />;
    case SensorLocationType.Basement:
      return <BrickWall />;
    case SensorLocationType.Attic:
      return <Package />;
    case SensorLocationType.ElectricalPanel:
      return <Zap />;
    case SensorLocationType.Custom:
      return <CircleDot />;
    default:
      return null;
  }
};

const mockedKpis = {
  leakDetected: true,
  energySaving: 123.31,
  lastUpdated: new Date(new Date().setHours(9, 30, 0, 0)),
};

const mockedSensors: Sensor[] = [
  {
    id: "sensor-1",
    hubId: "",
    serialNumber: "",
    type: "",
    name: "Humidity / Leak Sensor",
    location: "sensor-location-1",
    sublocation: "",
    status: SensorStatus.Active,
    lastReported: Date.now(),
    dsk: "",
    version: "",
  },
  {
    id: "sensor-2",
    hubId: "",
    serialNumber: "",
    type: "",
    name: "Power Monitor",
    location: "sensor-location-1",
    sublocation: "",
    status: SensorStatus.Active,
    lastReported: Date.now(),
    dsk: "",
    version: "",
  },
  {
    id: "sensor-3",
    hubId: "",
    serialNumber: "",
    type: "",
    name: "Kitchen Sink",
    location: "sensor-location-2",
    sublocation: "",
    status: SensorStatus.Active,
    lastReported: Date.now(),
    dsk: "",
    version: "",
  },
  {
    id: "sensor-4",
    hubId: "",
    serialNumber: "",
    type: "",
    name: "Humidity / Leak Sensor",
    location: "sensor-location-2",
    sublocation: "",
    status: SensorStatus.Active,
    lastReported: Date.now(),
    dsk: "",
    version: "",
  },
  {
    id: "sensor-5",
    hubId: "",
    serialNumber: "",
    type: "",
    name: "Humidity / Leak Sensor",
    location: "sensor-location-2",
    sublocation: "",
    status: SensorStatus.Active,
    lastReported: Date.now(),
    dsk: "",
    version: "",
  },
  {
    id: "sensor-6",
    hubId: "",
    serialNumber: "",
    type: "",
    name: "Humidity / Leak Sensor",
    location: "sensor-location-3",
    sublocation: "",
    status: SensorStatus.Active,
    lastReported: Date.now(),
    dsk: "",
    version: "",
  },
];

const mockedSensorLocations = [
  {
    id: "sensor-location-1",
    name: "Kitchen",
    type: SensorLocationType.Kitchen,
  },
  {
    id: "sensor-location-2",
    name: "Laundry",
    type: SensorLocationType.Laundry,
  },
  {
    id: "sensor-location-3",
    name: "HVAC",
    type: SensorLocationType.HVAC,
  },
  {
    id: "sensor-location-4",
    name: "Master Bedroom",
    type: SensorLocationType.MasterBedroom,
  },
  {
    id: "sensor-location-5",
    name: "Living Room",
    type: SensorLocationType.LivingRoom,
  },
  {
    id: "sensor-location-6",
    name: "Guest Bedroom",
    type: SensorLocationType.GuestBedroom,
  },
  {
    id: "sensor-location-7",
    name: "Bedroom",
    type: SensorLocationType.Bedroom,
  },
  {
    id: "sensor-location-8",
    name: "Dining Room",
    type: SensorLocationType.DiningRoom,
  },
  {
    id: "sensor-location-9",
    name: "Office",
    type: SensorLocationType.Office,
  },
  {
    id: "sensor-location-10",
    name: "Family Room",
    type: SensorLocationType.FamilyRoom,
  },
  {
    id: "sensor-location-11",
    name: "Main Floor",
    type: SensorLocationType.MainFloor,
  },
  {
    id: "sensor-location-12",
    name: "Garage",
    type: SensorLocationType.Garage,
  },
  {
    id: "sensor-location-13",
    name: "Basement",
    type: SensorLocationType.Basement,
  },
  {
    id: "sensor-location-14",
    name: "Attic",
    type: SensorLocationType.Attic,
  },
  {
    id: "sensor-location-15",
    name: "Electrical Panel",
    type: SensorLocationType.ElectricalPanel,
  },
  {
    id: "sensor-location-16",
    name: "Custom",
    type: SensorLocationType.Custom,
  },
];

const mockedSensorAlerts: SensorAlert[] = [
  {
    id: "sensor-alert-1",
    timestamp: Date.now(),
    sensorId: "sensor-2",
    hubId: "",
    alertType: SensorAlertType.HighCurrent,
    severity: SensorAlertSeverity.Medium,
    message: "Electrical Surge",
    value: "",
    status: "",
  },
  {
    id: "sensor-alert-2",
    timestamp: Date.now(),
    sensorId: "sensor-3",
    hubId: "",
    alertType: SensorAlertType.Leak,
    severity: SensorAlertSeverity.Critical,
    message: "Leak",
    value: "",
    status: "",
  },
];

export default Dashboard;
